import React, { useEffect, useState } from 'react';
import { FaUser, FaCalendarAlt, FaClock, FaComments, FaPaperPlane, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import "../styles/GetStarted.css";
import Footer from "../components/Footer.jsx";
import COLORS from '../styles/colors.js';

function Completed_Talkshow() {
    const navigate = useNavigate();
    const [talkshows, setTalkshows] = useState([]);
    const [filteredTalkshows, setFilteredTalkshows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeChats, setActiveChats] = useState({});
    const [chatMessages, setChatMessages] = useState({});
    const [inputTexts, setInputTexts] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [fetchedComments, setFetchedComments] = useState({});
    const [commentsLoading, setCommentsLoading] = useState({});
    const [commentsError, setCommentsError] = useState({});
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [expandedDescription, setExpandedDescription] = useState({}); // Track expanded descriptions
    const [categoriesMap, setCategoriesMap] = useState({});
    const [activeTab, setActiveTab] = useState('completed'); // 'completed' or 'upcoming'

    useEffect(() => {
        // Fetch categories first
        fetch(`${process.env.REACT_APP_BACKEND_API}/categories/`)
            .then((response) => response.json())
            .then((catsData) => {
                const map = {};
                (catsData || []).forEach((c) => {
                    const name = c.name || c.title || c.category_name || c.category || '';
                    map[c.id] = name;
                    map[String(c.id)] = name;
                });
                setCategoriesMap(map);
            })
            .catch((err) => console.error('Error fetching categories:', err));

        // Fetch shows
        fetch(`${process.env.REACT_APP_BACKEND_API}/shows/`)
            .then((response) => {
                if (!response.ok) throw new Error("Failed to fetch talk shows");
                return response.json();
            })
            .then((data) => {
                // Filter only public talk shows
                const publicTalkshows = data.filter(talkshow => !talkshow.is_private);

                setTalkshows(publicTalkshows);
                setFilteredTalkshows(publicTalkshows);

                const initialChats = {};
                const initialMessages = {};
                const initialInputs = {};

                publicTalkshows.forEach(talkshow => {
                    initialChats[talkshow.id] = false;
                    initialMessages[talkshow.id] = [];
                    initialInputs[talkshow.id] = "";
                });

                setActiveChats(initialChats);
                setChatMessages(initialMessages);
                setInputTexts(initialInputs);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    const fetchComments = async (show) => {
        try {
            setCommentsLoading(prev => ({ ...prev, [show]: true }));
            setCommentsError(prev => ({ ...prev, [show]: null }));

            const response = await fetch(`${process.env.REACT_APP_BACKEND_API}/shows/comments/${show}/`);
            if (!response.ok) throw new Error("Failed to fetch comments");
            const data = await response.json();

            // Normalize comments to an array (empty array if none)
            const comments = Array.isArray(data) ? data.map(comment => ({
                id: comment.id,
                userName: comment.userName,
                content: comment.content,
                createdAt: comment.created_at
            })) : [];

            setFetchedComments(prev => ({
                ...prev,
                [show]: comments
            }));
        } catch (error) {
            console.error('Error fetching comments:', error);
            setCommentsError(prev => ({ ...prev, [show]: error.message || 'Failed to load comments' }));
            setFetchedComments(prev => ({ ...prev, [show]: [] }));
        } finally {
            setCommentsLoading(prev => ({ ...prev, [show]: false }));
        }
    };

    const truncateWords = (text, wordLimit = 100) => {
        if (!text) return '';
        const words = text.split(/\s+/).filter(Boolean);
        if (words.length <= wordLimit) return text;
        return words.slice(0, wordLimit).join(' ') + '...';
    };

    // const toggleChat = (talkshowId) => {
    //     // Determine whether the chat will be open after toggling
    //     const willOpen = !activeChats[talkshowId];

    //     setActiveChats(prev => ({
    //         ...prev,
    //         [talkshowId]: willOpen
    //     }));

    //     // Fetch comments only when opening the chat
    //     if (willOpen) {
    //         fetchComments(talkshowId);
    //     }
    // };

    const handleSearch = () => {
        const lowerCaseSearch = searchTerm.toLowerCase().trim();

        if (!lowerCaseSearch) {
            setFilteredTalkshows(talkshows);
            return;
        }

        if (lowerCaseSearch.startsWith("#")) {
            const filtered = talkshows.filter((talkshow) => {
                if (!talkshow.description) return false;

                const descriptionLower = talkshow.description.toLowerCase();
                const hashtags = descriptionLower.match(/#[a-z0-9_]+/g) || [];

                return hashtags.some(tag => tag === lowerCaseSearch);
            });
            setFilteredTalkshows(filtered);
        } else {
            const filtered = talkshows.filter((talkshow) => {
                if (talkshow.host?.userName?.toLowerCase().includes(lowerCaseSearch)) {
                    return true;
                }

                if (talkshow.co_hosts?.some(coHost =>
                    coHost.userName?.toLowerCase().includes(lowerCaseSearch)
                )) {
                    return true;
                }

                if (talkshow.title?.toLowerCase().includes(lowerCaseSearch)) {
                    return true;
                }

                return false;
            });
            setFilteredTalkshows(filtered);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!value.trim()) {
            setFilteredTalkshows(talkshows);
        }
    };

    const getCategoryName = (talkshow) => {
        const catId = talkshow.category || talkshow.category_id || talkshow.categoryId || talkshow.category_id?.toString?.();
        if (!catId) return '';
        return categoriesMap[catId] || categoriesMap[String(catId)] || String(catId);
    };

    if (loading) return <p className="loading-message">Loading...</p>;
    if (error) return <p className="error-message">{error}</p>;

    const getShowDateTime = (talkshow) => {
        if (!talkshow.show_date) return null;
        
        let time = talkshow.start_time;
        if (!time) {
            // No time specified, assume end of day for comparison (so it stays upcoming today)
            time = "23:59:59";
        }
        
        // Try ISO format first
        let dateStr = `${talkshow.show_date}T${time}`;
        let date = new Date(dateStr);
        
        if (isNaN(date.getTime())) {
            // Try with space instead of T
            dateStr = `${talkshow.show_date} ${time}`;
            date = new Date(dateStr);
        }
        
        if (isNaN(date.getTime())) {
             // If time causes issues, try just date (midnight)
             // But wait, if we ignore time, we might mark today's show as completed if we use midnight.
             // If time is invalid, maybe assume end of day?
             date = new Date(`${talkshow.show_date}T23:59:59`);
        }

        return isNaN(date.getTime()) ? null : date;
    };

    const now = new Date();

    // Filter for expired/completed talkshows
    const completedTalkshows = filteredTalkshows.filter(talkshow => {
        const showDate = getShowDateTime(talkshow);
        if (!showDate) return false; // No date -> not completed
        return showDate < now;
    });

    // Filter for upcoming talkshows
    const upcomingTalkshows = filteredTalkshows.filter(talkshow => {
        const showDate = getShowDateTime(talkshow);
        if (!showDate) return true; // No date -> assume upcoming/TBD
        return showDate >= now;
    });

    // Get shows based on active tab
    const displayedTalkshows = activeTab === 'completed' ? completedTalkshows : upcomingTalkshows;

    return (
        <div className="get-started-container" style={{
            border: isDarkMode ? 'none' : '1px solid #f0f0f0'
        }}>

            {/* hero section*/}
            <div
                className="container d-flex flex-column align-items-center justify-content-start"
                style={{ marginTop: "30px", textAlign: "center", marginBottom: "30px" }}
            >
                <h1
                    style={{
                        fontSize: "36px",
                        fontWeight: "bold",
                        color: COLORS.primary,
                    }}
                >
                    Talk Shows
                </h1>
                <p
                    style={{
                        fontSize: "20px",
                        color: COLORS.text_muted,
                        maxWidth: "800px",
                        marginTop: "8px",
                    }}
                >
                    Discover engaging discussions on topics that matter
                </p>

            </div>

            {/* Tabs */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '30px',
                padding: '0 20px'
            }}>
                <button
                    onClick={() => setActiveTab('completed')}
                    style={{
                        padding: '12px 30px',
                        fontSize: '16px',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: activeTab === 'completed' ? COLORS.primary : '#f0f0f0',
                        color: activeTab === 'completed' ? '#fff' : '#666',
                        transition: 'all 0.3s ease',
                        boxShadow: activeTab === 'completed' ? '0 4px 12px rgba(13, 110, 253, 0.3)' : 'none'
                    }}
                >
                    Completed Shows ({completedTalkshows.length})
                </button>
                <button
                    onClick={() => setActiveTab('upcoming')}
                    style={{
                        padding: '12px 30px',
                        fontSize: '16px',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: activeTab === 'upcoming' ? COLORS.primary : '#f0f0f0',
                        color: activeTab === 'upcoming' ? '#fff' : '#666',
                        transition: 'all 0.3s ease',
                        boxShadow: activeTab === 'upcoming' ? '0 4px 12px rgba(13, 110, 253, 0.3)' : 'none'
                    }}
                >
                    Upcoming Shows ({upcomingTalkshows.length})
                </button>
            </div>

            <div className="talkshow-container">
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '20px',
                    padding: '20px',
                    maxWidth: '1400px',
                    margin: '0 auto'
                }}>
                    {displayedTalkshows.length === 0 ? (
                        <p className="no-results" style={{ color: 'black' }}>
                            {activeTab === 'completed' ? 'No completed shows found' : 'No upcoming shows found'}
                        </p>
                    ) : (
                        displayedTalkshows.map((talkshow) => {
                            const sub = talkshow.subtopics?.[0];
                            return (
                            <div 
                                key={talkshow.id} 
                                className="grid-tile" 
                                onClick={() => navigate(`/read-more/${talkshow.id}`)}
                                style={{
                                    background: '#fff',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    boxShadow: '0 6px 18px rgba(11,22,39,0.04)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: '300px'
                                }}
                            >
                                {sub?.image_urls?.[0] ? (
                                    <img 
                                        src={sub.image_urls[0]} 
                                        alt={talkshow.title}
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover',
                                            display: 'block'
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        background: '#e9edf5',
                                        width: '100%',
                                        height: '200px',
                                        minHeight: '200px'
                                    }} />
                                )}
                                <div style={{
                                    padding: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    flex: 1
                                }}>
                                    <div>
                                        {getCategoryName(talkshow) && (
                                            <span style={{
                                                background: '#eef2ff',
                                                color: '#0d6efd',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                display: 'inline-block',
                                                marginBottom: '10px'
                                            }}>
                                                {getCategoryName(talkshow)}
                                            </span>
                                        )}
                                        <h4 style={{
                                            margin: '0 0 12px',
                                            fontSize: '18px',
                                            color: '#111',
                                            lineHeight: '1.3'
                                        }}>
                                            {talkshow.title ? talkshow.title.charAt(0).toUpperCase() + talkshow.title.slice(1) : ''}
                                        </h4>
                                    </div>
                                    <p style={{
                                        fontSize: '13px',
                                        color: '#6b6b72',
                                        margin: 0
                                    }}>
                                        {talkshow.show_date} ({activeTab === 'completed' ? 'Completed' : 'Upcoming'})
                                    </p>
                                </div>
                            </div>
                        );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

export default Completed_Talkshow;